import type { ReactNode } from "react";

import { Modal } from "./modal";
import styles from "./confirm-dialog.module.css";

/**
 * "Are you sure?", drawn rather than thrown at the browser.
 *
 * ── Why this exists (Anthony's QA, items 5, 6 and 18) ───────────────────────
 *
 * Three of his findings resolve to the same missing thing: a refusal that
 * offers a way out of itself.
 *
 *   - a plan's visit could not be charted until the one before it was ticked
 *     off, and the refusal was a sentence with no way to tick it;
 *   - a shared phone number needed a checkbox found and a second save;
 *   - a past rota day changed with no resistance at all.
 *
 * Each wants the same shape: say what is in the way, and put the action that
 * clears it next to the sentence saying so. The client's words on the first
 * were *"make sure the notification is visible (popup or anything you deem
 * fit)"*.
 *
 * ── Why not `window.confirm` ────────────────────────────────────────────────
 *
 * Four call sites already use it and it is the wrong tool for all of them. It
 * cannot carry a named action — every button is *OK* — so "Mark visit 1
 * complete" would have to be smuggled into the message and the reader left to
 * infer which button does it. It cannot be styled, cannot be tested through the
 * DOM, and on some browsers a second one is suppressed entirely after the
 * first, so a refusal can silently not appear.
 *
 * ── Built on `Modal`, so it dismisses like everything else ──────────────────
 *
 * Backdrop and Escape both cancel, which is what the client asked for across
 * the product in the previous QA round. **Cancelling is always the safe
 * outcome**: `onCancel` never performs the action, so a dialog dismissed by a
 * stray click has done nothing.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "normal",
  busy = false,
  onConfirm,
  onCancel,
  testId,
}: {
  title: string;
  /** The body. A sentence or two — a dialog nobody reads is a dialog nobody heeds. */
  children: ReactNode;
  /**
   * **Names the action, never "OK"** — "Add anyway", "Mark visit 1 complete",
   * "Delete this plan". A reader should be able to tell what pressing it does
   * without re-reading the paragraph above it.
   */
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` for anything that destroys or cannot be undone. */
  tone?: "normal" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
}): React.JSX.Element {
  return (
    <Modal label={title} onClose={onCancel} testId={testId}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.body}>{children}</div>

      <div className={styles.actions}>
        {/*
          **Cancel first in the DOM, confirm second.** Tab reaches the safe
          option before the destructive one, and the visual order below puts
          the confirm on the right where a reader looks for it.
        */}
        <button
          type="button"
          className={styles.cancel}
          onClick={onCancel}
          data-testid="confirm-cancel"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={tone === "danger" ? styles.danger : styles.confirm}
          disabled={busy}
          onClick={onConfirm}
          data-testid="confirm-accept"
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
